/**
 * Export card: pick a game, pick a format up front (Hudl compatible, Custom,
 * or a saved template) and download. The file is fetched right away — there
 * is nothing to wait for and nothing to come back to.
 */
import { useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import { ApiError } from '../../lib/api-error';
import type {
  HudlExportColumn,
  HudlExportFieldCatalog,
  HudlExportTemplate,
  HudlFieldGroup,
  HudlFileType,
  TeamEvent,
} from '../../lib/api-types';

import {
  defaultCustomColumns,
  eventOptionLabel,
  fallbackExportName,
  saveBlob,
  toMessage,
} from './hudl-format';

const GROUP_LABELS: Record<HudlFieldGroup, string> = {
  situation: 'Situation',
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special teams',
  custom: 'Your team stats',
};

interface DraftColumn extends HudlExportColumn {
  key: string;
  isIncluded: boolean;
}

type FormatChoice = 'hudl' | 'custom' | `template:${string}`;

let draftKey = 0;
function toDraft(columns: readonly HudlExportColumn[]): DraftColumn[] {
  return columns.map((column) => {
    draftKey += 1;
    return { ...column, key: `c${draftKey}`, isIncluded: true };
  });
}

function includedColumns(draft: readonly DraftColumn[]): HudlExportColumn[] {
  return draft
    .filter((column) => column.isIncluded)
    .map((column) => ({ field: column.field, header: column.header.trim() || column.field }));
}

function sameColumns(
  left: readonly HudlExportColumn[],
  right: readonly HudlExportColumn[]
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (column, index) =>
        column.field === right[index].field && column.header === right[index].header
    )
  );
}

export interface ExportCardProps {
  events: readonly TeamEvent[];
  fields: HudlExportFieldCatalog;
  templates: readonly HudlExportTemplate[];
  setTemplates(update: (current: HudlExportTemplate[]) => HudlExportTemplate[]): void;
}

export function ExportCard({
  events,
  fields,
  templates,
  setTemplates,
}: ExportCardProps): JSX.Element {
  const api = useApiClient();
  const { showToast } = useToast();
  const ids = useId();
  const [eventId, setEventId] = useState(events[0]?.event_id ?? '');
  const [choice, setChoice] = useState<FormatChoice>('hudl');
  const [draft, setDraft] = useState<DraftColumn[]>(() =>
    toDraft(defaultCustomColumns(fields.hudl_columns, fields.items))
  );
  const [fileType, setFileType] = useState<HudlFileType>('xlsx');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  useEffect(() => {
    if (eventId === '' && events.length > 0) setEventId(events[0].event_id);
  }, [events, eventId]);

  const event = events.find((entry) => entry.event_id === eventId) ?? null;
  const activeTemplate = choice.startsWith('template:')
    ? (templates.find((template) => `template:${template.id}` === choice) ?? null)
    : null;

  function choose(next: FormatChoice): void {
    setChoice(next);
    setDownloadError(null);
    if (next === 'custom') {
      setDraft(toDraft(defaultCustomColumns(fields.hudl_columns, fields.items)));
      setFileType('xlsx');
    } else if (next.startsWith('template:')) {
      const template = templates.find((entry) => `template:${entry.id}` === next);
      if (template) {
        setDraft(toDraft(template.columns));
        setFileType(template.file_type);
      }
    }
  }

  const draftColumns = includedColumns(draft);
  const isTemplateChanged =
    activeTemplate !== null &&
    (!sameColumns(draftColumns, activeTemplate.columns) || fileType !== activeTemplate.file_type);

  async function download(): Promise<void> {
    if (event === null) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const isHudl = choice === 'hudl';
      const file = await api.downloadHudlExport(
        isHudl
          ? { event_id: event.event_id, format: 'hudl', file_type: 'xlsx' }
          : activeTemplate !== null && !isTemplateChanged
            ? {
                event_id: event.event_id,
                format: 'custom',
                template_id: activeTemplate.id,
                file_type: fileType,
              }
            : {
                event_id: event.event_id,
                format: 'custom',
                columns: draftColumns,
                file_type: fileType,
              }
      );
      const name = file.file_name ?? fallbackExportName(event, isHudl ? 'xlsx' : fileType);
      saveBlob(file.blob, name);
      setLastFile(name);
    } catch (caught) {
      setDownloadError(toMessage(caught, 'The file could not be created. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  }

  const formatOptions: { value: FormatChoice; label: string }[] = [
    { value: 'hudl', label: 'Hudl compatible' },
    { value: 'custom', label: 'Custom' },
    ...templates.map((template) => ({
      value: `template:${template.id}` as FormatChoice,
      label: template.name,
    })),
  ];

  const isCustomBlocked = choice !== 'hudl' && draftColumns.length === 0;

  return (
    <DeskSection
      title="Export a game"
      description="Download a game’s plays as a spreadsheet you can load into Hudl."
      className="hudl-card"
      data-testid="hudl-export"
    >
      {events.length === 0 ? (
        <p className="hudl-empty">
          You have no games yet. Once a game has plays, you can export it here.
        </p>
      ) : (
        <>
          <div className="hudl-export__row">
            <label className="field hudl-export__game">
              <span className="field__label">Game</span>
              <select
                className="field__control"
                value={eventId}
                onChange={(changeEvent) => setEventId(changeEvent.target.value)}
                data-testid="hudl-export-event"
              >
                {events.map((entry) => (
                  <option key={entry.event_id} value={entry.event_id}>
                    {eventOptionLabel(entry)}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="hudl-choice">
              <legend className="field__label">Format</legend>
              <div className="hudl-choice__options" data-testid="hudl-export-format">
                {formatOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`hudl-choice__option${choice === option.value ? ' is-active' : ''}`}
                  >
                    <input
                      type="radio"
                      className="visually-hidden"
                      name={`${ids}-format`}
                      value={option.value}
                      checked={choice === option.value}
                      onChange={() => choose(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {choice === 'hudl' ? (
            <div className="hudl-format" data-testid="hudl-format-hudl">
              <p className="hudl-format__summary">
                Ready to load into Hudl: the same spreadsheet layout Hudl exports, one row per play.
              </p>
              <details className="hudl-format__cols">
                <summary>See the {fields.hudl_columns.length} columns</summary>
                <ul className="hudl-chips" aria-label="Columns in the file">
                  {fields.hudl_columns.map((column) => (
                    <li key={column.header} className="hudl-chip">
                      {column.header}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          ) : (
            <CustomEditor
              fields={fields}
              draft={draft}
              setDraft={setDraft}
              fileType={fileType}
              setFileType={setFileType}
              activeTemplate={activeTemplate}
              isTemplateChanged={isTemplateChanged}
              onSaved={(template, isNew) => {
                setTemplates((current) =>
                  isNew
                    ? [...current, template]
                    : current.map((entry) => (entry.id === template.id ? template : entry))
                );
                setChoice(`template:${template.id}`);
                showToast(
                  isNew ? `Saved “${template.name}”.` : `Updated “${template.name}”.`,
                  'success'
                );
              }}
              onDeleted={(template) => {
                setTemplates((current) => current.filter((entry) => entry.id !== template.id));
                setChoice('custom');
                showToast(`Deleted “${template.name}”.`, 'success');
              }}
            />
          )}

          <div className="hudl-card__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void download()}
              disabled={event === null || isDownloading || isCustomBlocked}
              aria-busy={isDownloading}
              data-testid="hudl-download"
            >
              {isDownloading ? <span className="hudl-spinner" aria-hidden="true" /> : null}
              {isDownloading ? 'Preparing file…' : 'Download'}
            </button>
            {isCustomBlocked ? (
              <span className="muted">Include at least one column.</span>
            ) : lastFile !== null && !isDownloading && downloadError === null ? (
              <span className="hudl-card__done" role="status" title={lastFile}>
                Downloaded {lastFile}
              </span>
            ) : null}
          </div>
          {downloadError === null ? null : (
            <p className="field__error" role="alert" data-testid="hudl-download-error">
              {downloadError}
            </p>
          )}
        </>
      )}
    </DeskSection>
  );
}

interface CustomEditorProps {
  fields: HudlExportFieldCatalog;
  draft: DraftColumn[];
  setDraft(update: (current: DraftColumn[]) => DraftColumn[]): void;
  fileType: HudlFileType;
  setFileType(value: HudlFileType): void;
  activeTemplate: HudlExportTemplate | null;
  isTemplateChanged: boolean;
  onSaved(template: HudlExportTemplate, isNew: boolean): void;
  onDeleted(template: HudlExportTemplate): void;
}

function CustomEditor({
  fields,
  draft,
  setDraft,
  fileType,
  setFileType,
  activeTemplate,
  isTemplateChanged,
  onSaved,
  onDeleted,
}: CustomEditorProps): JSX.Element {
  const api = useApiClient();
  const ids = useId();
  const [fieldToAdd, setFieldToAdd] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [isNaming, setIsNaming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setIsNaming(false);
    setSaveError(null);
    setTemplateName(activeTemplate?.name ?? '');
  }, [activeTemplate]);

  const labels = useMemo(
    () => new Map(fields.items.map((field) => [field.field, field.label])),
    [fields.items]
  );
  const available = fields.items.filter(
    (field) => !draft.some((column) => column.field === field.field)
  );
  const groups = (Object.keys(GROUP_LABELS) as HudlFieldGroup[])
    .map((group) => ({ group, items: available.filter((field) => field.group === group) }))
    .filter((entry) => entry.items.length > 0);

  function update(key: string, patch: Partial<DraftColumn>): void {
    setDraft((current) =>
      current.map((column) => (column.key === key ? { ...column, ...patch } : column))
    );
  }

  function move(index: number, step: -1 | 1): void {
    setDraft((current) => {
      const target = index + step;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function remove(key: string): void {
    setDraft((current) => current.filter((column) => column.key !== key));
  }

  function addField(): void {
    const field = fields.items.find((entry) => entry.field === fieldToAdd);
    if (!field) return;
    setDraft((current) => [
      ...current,
      ...toDraft([{ field: field.field, header: field.default_header }]),
    ]);
    setFieldToAdd('');
  }

  async function save(asNew: boolean): Promise<void> {
    const name = templateName.trim();
    if (asNew && name === '') {
      setSaveError('Give the template a name.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const columns = includedColumns(draft);
      if (asNew || activeTemplate === null) {
        const created = await api.createHudlExportTemplate({ name, file_type: fileType, columns });
        onSaved(created, true);
      } else {
        const updated = await api.updateHudlExportTemplate(activeTemplate.id, {
          name: name === '' ? activeTemplate.name : name,
          file_type: fileType,
          columns,
        });
        onSaved(updated, false);
      }
      setIsNaming(false);
    } catch (caught) {
      if (ApiError.isApiError(caught) && caught.code === 'DUPLICATE_PRESET_NAME') {
        setSaveError(`You already have a template called “${name}”. Pick another name.`);
      } else {
        setSaveError(toMessage(caught, 'The template could not be saved. Please try again.'));
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (activeTemplate === null) return;
    setDeleteError(null);
    try {
      await api.removeHudlExportTemplate(activeTemplate.id);
      setIsConfirmingDelete(false);
      onDeleted(activeTemplate);
    } catch (caught) {
      setDeleteError(toMessage(caught, 'The template could not be deleted.'));
    }
  }

  const isRenamed = activeTemplate !== null && templateName.trim() !== activeTemplate.name;
  const includedCount = draft.filter((column) => column.isIncluded).length;

  return (
    <div className="hudl-editor" data-testid="hudl-custom-editor">
      <div className="hudl-editor__bar">
        <p className="hudl-format__summary">
          <strong>
            {includedCount} of {draft.length} columns
          </strong>{' '}
          included
        </p>
        <div className="hudl-filetype" role="group" aria-label="File type">
          {(['xlsx', 'csv'] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={`hudl-filetype__option${fileType === type ? ' is-active' : ''}`}
              aria-pressed={fileType === type}
              onClick={() => setFileType(type)}
            >
              {type === 'xlsx' ? 'Excel (.xlsx)' : 'CSV (.csv)'}
            </button>
          ))}
        </div>
      </div>

      <ol className="hudl-columns" aria-label="Columns in the file">
        {draft.map((column, index) => (
          <li
            key={column.key}
            className={`hudl-column${column.isIncluded ? '' : ' is-excluded'}`}
            data-field={column.field}
          >
            <input
              type="checkbox"
              className="hudl-column__check"
              checked={column.isIncluded}
              onChange={(changeEvent) =>
                update(column.key, { isIncluded: changeEvent.target.checked })
              }
              aria-label={`Include ${labels.get(column.field) ?? column.field}`}
            />
            <div className="hudl-column__names">
              <input
                type="text"
                className="hudl-column__header"
                value={column.header}
                maxLength={60}
                onChange={(changeEvent) => update(column.key, { header: changeEvent.target.value })}
                aria-label={`Column heading for ${labels.get(column.field) ?? column.field}`}
              />
              <span className="hudl-column__field">{labels.get(column.field) ?? column.field}</span>
            </div>
            <div className="hudl-column__tools">
              <button
                type="button"
                className="hudl-icon-btn"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${column.header} up`}
                title="Move up"
              >
                <span aria-hidden="true">↑</span>
              </button>
              <button
                type="button"
                className="hudl-icon-btn"
                onClick={() => move(index, 1)}
                disabled={index === draft.length - 1}
                aria-label={`Move ${column.header} down`}
                title="Move down"
              >
                <span aria-hidden="true">↓</span>
              </button>
              <button
                type="button"
                className="hudl-icon-btn"
                onClick={() => remove(column.key)}
                aria-label={`Remove ${column.header}`}
                title="Remove"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </li>
        ))}
      </ol>

      {groups.length === 0 ? null : (
        <div className="hudl-editor__add">
          <label className="field hudl-editor__add-field">
            <span className="visually-hidden">Add a column</span>
            <select
              className="field__control"
              value={fieldToAdd}
              onChange={(changeEvent) => setFieldToAdd(changeEvent.target.value)}
              data-testid="hudl-add-field"
            >
              <option value="">Add a column…</option>
              {groups.map((entry) => (
                <optgroup key={entry.group} label={GROUP_LABELS[entry.group]}>
                  {entry.items.map((field) => (
                    <option key={field.field} value={field.field}>
                      {field.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={addField}
            disabled={fieldToAdd === ''}
          >
            Add
          </button>
        </div>
      )}

      <div className="hudl-template">
        {activeTemplate === null ? (
          isNaming ? (
            <div className="hudl-template__row">
              <label className="field hudl-template__name">
                <span className="field__label">Template name</span>
                <input
                  id={`${ids}-name`}
                  className="field__control"
                  value={templateName}
                  maxLength={80}
                  placeholder="e.g. Defense breakdown"
                  onChange={(changeEvent) => setTemplateName(changeEvent.target.value)}
                  autoFocus
                />
              </label>
              <div className="hudl-template__buttons">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => void save(true)}
                  disabled={isSaving}
                  data-testid="hudl-template-save"
                >
                  {isSaving ? 'Saving…' : 'Save template'}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setIsNaming(false);
                    setSaveError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ghost hudl-template__start"
              onClick={() => setIsNaming(true)}
              data-testid="hudl-template-new"
            >
              Save as template…
            </button>
          )
        ) : (
          <div className="hudl-template__row">
            <label className="field hudl-template__name">
              <span className="field__label">Template name</span>
              <input
                className="field__control"
                value={templateName}
                maxLength={80}
                onChange={(changeEvent) => setTemplateName(changeEvent.target.value)}
              />
            </label>
            <div className="hudl-template__buttons">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void save(false)}
                disabled={isSaving || (!isTemplateChanged && !isRenamed)}
                data-testid="hudl-template-update"
              >
                {isSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn btn--ghost hudl-danger-text"
                onClick={() => setIsConfirmingDelete(true)}
              >
                Delete
              </button>
            </div>
          </div>
        )}
        {saveError === null ? null : (
          <p className="field__error" role="alert">
            {saveError}
          </p>
        )}
      </div>

      {isConfirmingDelete && activeTemplate !== null ? (
        <ConfirmDialog
          title="Delete this template?"
          consequence={`“${activeTemplate.name}” will no longer be offered as an export format. Files you already downloaded are not affected.`}
          confirmLabel="Delete template"
          tone="danger"
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => {
            setIsConfirmingDelete(false);
            setDeleteError(null);
          }}
        />
      ) : null}
    </div>
  );
}
